// src/App.tsx (or your main authenticated layout component)
import { Outlet } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useEffect, useState } from 'react';
import { initWebSocket, disconnectWebSocket } from './lib/websocketService';
import { useAuth } from './contexts/AuthContext'; // Import useAuth
import Sidebar from './components/layout/Sidebar';
import Header from './components/layout/Header';

export type SidebarState = 'open' | 'narrow' | 'closed';

function App() {
  const { isAuthenticated, isLoading } = useAuth(); // Get auth state

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      // User is authenticated and auth state is loaded.
      // WebSocket connection will use the HttpOnly cookie for authentication.
      initWebSocket();

      // Return cleanup function to disconnect WebSocket when component unmounts
      // or when isAuthenticated/isLoading changes trigger a re-evaluation and this condition is no longer met.
      return () => {
        disconnectWebSocket();
      };
    }
    // If the condition is not met (e.g., user logs out, isAuthenticated becomes false),
    // the cleanup function from the *previous* render (if any, where isAuthenticated was true)
    // will be executed, disconnecting the WebSocket.
    // If the condition was never met, initWebSocket() is not called, and cleanup does nothing extra.
  }, [isAuthenticated, isLoading]); // Re-run effect if auth state or loading status changes

  const [sidebarState, setSidebarState] = useState<SidebarState>('open');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarState('closed'); // Default to closed on mobile
      } else {
        // Restore last desktop state or default to 'open'
        const lastDesktopState = localStorage.getItem('sidebarDesktopState') as SidebarState | null;
        setSidebarState(lastDesktopState || 'open');
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Persist desktop sidebar state
    if (!isMobile) {
      localStorage.setItem('sidebarDesktopState', sidebarState);
    }
  }, [sidebarState, isMobile]);

  const toggleSidebarMobile = () => { // For hamburger menu on mobile
    setSidebarState(prev => prev === 'closed' ? 'open' : 'closed');
  };

  const toggleSidebarDesktop = () => { // For desktop open/narrow toggle
    if (!isMobile) {
      setSidebarState(prev => prev === 'open' ? 'narrow' : 'open');
    }
  };
  
  const setSidebarViewClosed = () => { // Explicitly set to open (e.g. when mobile navlink clicked)
    if (isMobile) setSidebarState('closed'); // Auto-close on mobile after nav
  }

  // Determine main content margin based on sidebar state
  let mainContentMargin = 'md:ml-64'; // Default for open sidebar
  if (isMobile || sidebarState === 'closed') {
    mainContentMargin = 'ml-0';
  } else if (sidebarState === 'narrow') {
    mainContentMargin = 'md:ml-20'; // Margin for narrow sidebar (e.g., 5rem = 80px, tailwind ml-20)
  }

  return (
    <>
      <Toaster position="top-right" />
      <div className="flex h-screen flex-col bg-gray-100"> {/* Updated background */}
            <Header 
              onToggleSidebarMobile={toggleSidebarMobile} 
              onToggleSidebarDesktop={toggleSidebarDesktop}
              sidebarState={sidebarState}
              isMobile={isMobile}
            />
            <div className="flex flex-1 overflow-hidden">
              <Sidebar 
                sidebarState={sidebarState} 
                setSidebarViewClosed={setSidebarViewClosed} 
                onToggleDesktop={toggleSidebarDesktop} // Pass this for the new button in sidebar
                isMobile={isMobile}
              />
              <main className={`flex-1 overflow-y-auto p-4 md:p-6 lg:p-8 transition-all duration-300 ease-in-out ${mainContentMargin}`}>
                <Outlet />
              </main>
            </div>
          </div>
      
    </>
  );
}
export default App; 
